import {
  getLedgerSafePostgresTrialHead,
  postLedgerSafePostgresTrialEdit,
  type LedgerSafePostgresTrialAPIError,
  type LedgerSafePostgresTrialOperationKind,
  type LedgerSafePostgresTrialRequestOptions,
} from '../api/ledgerSafePostgresTrialAPI'
import type { LedgerSafeEditorialEvent } from '../types/ledgerSafeEditorialEvents'

export const LEDGER_SAFE_POSTGRES_TRIAL_SESSION_ID_KEY =
  'ledger-safe.postgres-trial.disposable-id.v1'
export const LEDGER_SAFE_POSTGRES_TRIAL_TELEMETRY_EVENT =
  'ledger-safe:postgres-trial-shadow'

const SUPPORTED_OPERATION_KINDS = new Set<LedgerSafePostgresTrialOperationKind>([
  'relocate_cells',
  'boundary_shift',
  'merge_units',
])

const DEFAULT_MAX_QUEUE_DEPTH = 8
const MAX_QUEUE_DEPTH = 64
const MAX_RESULT_MESSAGE_LENGTH = 240
const MAX_TELEMETRY_ID_LENGTH = 120
const MAX_TELEMETRY_DURATION_MS = 120_000

type TrialSessionStorage = Pick<Storage, 'getItem' | 'setItem'>
type TrialTelemetryTarget = Pick<EventTarget, 'dispatchEvent'>

export interface LedgerSafePostgresTrialShadowInput {
  enabled: boolean
  event: LedgerSafeEditorialEvent
  trialId?: string
}

interface LedgerSafePostgresTrialShadowResultBase {
  operationId: string
  chapterId: string
  trialId: string | null
}

export type LedgerSafePostgresTrialShadowResult =
  | (LedgerSafePostgresTrialShadowResultBase & {
    status: 'mirrored'
    revision: number
    duplicate: boolean
    conflictRecovered: boolean
    postAttempts: 1 | 2
    requestBytes: number
    serverTransactionMs?: number
  })
  | (LedgerSafePostgresTrialShadowResultBase & {
    status: 'skipped'
    reason:
      | 'circuit_open'
      | 'disabled'
      | 'queue_full'
      | 'unsupported_operation'
  })
  | (LedgerSafePostgresTrialShadowResultBase & {
    status: 'failed'
    error: LedgerSafePostgresTrialAPIError
    postAttempts: 0 | 1 | 2
  })

export type LedgerSafePostgresTrialShadowObserver = (
  result: LedgerSafePostgresTrialShadowResult,
) => void

export interface LedgerSafePostgresTrialShadowOptions
  extends LedgerSafePostgresTrialRequestOptions {
  storage?: TrialSessionStorage | null
  idFactory?: () => string
  observer?: LedgerSafePostgresTrialShadowObserver
  maxQueueDepth?: number
  deferDispatch?: (callback: () => void) => void
  now?: () => number
  telemetryTarget?: TrialTelemetryTarget | null
}

export interface LedgerSafePostgresTrialShadow {
  /**
   * Adds an edit to a bounded per-trial, per-chapter queue. The returned
   * promise always resolves; callers on the live edit path may intentionally
   * discard it with `void`.
   */
  enqueue: (
    input: LedgerSafePostgresTrialShadowInput,
  ) => Promise<LedgerSafePostgresTrialShadowResult>
}

const boundedMessage = (value: unknown): string => {
  const raw = value instanceof Error
    ? value.message
    : typeof value === 'string'
      ? value
      : 'PostgreSQL trial shadow failed.'
  const normalized = raw.trim() || 'PostgreSQL trial shadow failed.'
  return normalized.length > MAX_RESULT_MESSAGE_LENGTH
    ? `${normalized.slice(0, MAX_RESULT_MESSAGE_LENGTH - 1)}…`
    : normalized
}

const unexpectedError = (error: unknown): LedgerSafePostgresTrialAPIError => ({
  code: 'network',
  message: boundedMessage(error),
  status: null,
})

const randomTrialId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `ledger-safe-pg-trial-${crypto.randomUUID()}`
    }
  } catch {
    // Fall through to the dependency-free session identifier.
  }
  return `ledger-safe-pg-trial-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

const defaultSessionStorage = (): TrialSessionStorage | null => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null
  } catch {
    return null
  }
}

export const getOrCreateLedgerSafePostgresTrialId = (
  storage: TrialSessionStorage | null = defaultSessionStorage(),
  idFactory: () => string = randomTrialId,
): string => {
  if (storage) {
    try {
      const stored = storage.getItem(LEDGER_SAFE_POSTGRES_TRIAL_SESSION_ID_KEY)?.trim()
      if (stored) {
        return stored
      }
    } catch {
      // Storage is best-effort; the in-memory client still retains the new id.
    }
  }

  let trialId: string
  try {
    trialId = idFactory()
  } catch {
    trialId = randomTrialId()
  }
  if (storage) {
    try {
      storage.setItem(LEDGER_SAFE_POSTGRES_TRIAL_SESSION_ID_KEY, trialId)
    } catch {
      // A disposable shadow trial must never interrupt the authoritative edit.
    }
  }
  return trialId
}

const asSupportedKind = (
  kind: LedgerSafeEditorialEvent['operation']['kind'],
): LedgerSafePostgresTrialOperationKind | null =>
  SUPPORTED_OPERATION_KINDS.has(kind as LedgerSafePostgresTrialOperationKind)
    ? kind as LedgerSafePostgresTrialOperationKind
    : null

const queueKey = (trialId: string, chapterId: string): string =>
  `${trialId}\u0000${chapterId}`

const boundedInteger = (
  value: number | undefined,
  fallback: number,
  minimum: number,
  maximum: number,
): number => (
  typeof value === 'number' && Number.isFinite(value)
    ? Math.min(maximum, Math.max(minimum, Math.floor(value)))
    : fallback
)

const boundedDuration = (value: number): number =>
  Math.min(
    MAX_TELEMETRY_DURATION_MS,
    Math.max(0, Math.round(Number.isFinite(value) ? value : 0)),
  )

const boundedTelemetryId = (value: string | null): string | null => {
  if (value === null) {
    return null
  }
  return value.length > MAX_TELEMETRY_ID_LENGTH
    ? value.slice(0, MAX_TELEMETRY_ID_LENGTH)
    : value
}

const defaultTelemetryTarget = (): TrialTelemetryTarget | null => {
  if (
    process.env.NODE_ENV === 'production' ||
    typeof window === 'undefined'
  ) {
    return null
  }
  return window
}

const defaultDeferDispatch = (callback: () => void): void => {
  if (
    typeof window !== 'undefined' &&
    typeof window.requestAnimationFrame === 'function'
  ) {
    window.requestAnimationFrame(() => {
      globalThis.setTimeout(callback, 0)
    })
    return
  }
  globalThis.setTimeout(callback, 0)
}

const createTelemetryEvent = (
  detail: Record<string, unknown>,
): Event | null => {
  try {
    if (typeof CustomEvent === 'function') {
      return new CustomEvent(LEDGER_SAFE_POSTGRES_TRIAL_TELEMETRY_EVENT, {
        detail,
      })
    }
    if (typeof Event === 'function') {
      const event = new Event(LEDGER_SAFE_POSTGRES_TRIAL_TELEMETRY_EVENT)
      Object.defineProperty(event, 'detail', { value: detail })
      return event
    }
  } catch {
    // Development telemetry must never affect either persistence path.
  }
  return null
}

export const createLedgerSafePostgresTrialShadow = (
  options: LedgerSafePostgresTrialShadowOptions = {},
): LedgerSafePostgresTrialShadow => {
  const queueTails = new Map<string, Promise<void>>()
  const pendingCounts = new Map<string, number>()
  const headRevisions = new Map<string, number>()
  const openCircuits = new Set<string>()
  const storage = options.storage === undefined ? defaultSessionStorage() : options.storage
  const maxQueueDepth = boundedInteger(
    options.maxQueueDepth,
    DEFAULT_MAX_QUEUE_DEPTH,
    1,
    MAX_QUEUE_DEPTH,
  )
  const deferDispatch = options.deferDispatch || defaultDeferDispatch
  const now = options.now || (() => (
    typeof performance !== 'undefined' ? performance.now() : Date.now()
  ))
  const telemetryTarget = options.telemetryTarget === undefined
    ? defaultTelemetryTarget()
    : options.telemetryTarget
  let generatedTrialId: string | null = null

  const notify = (
    result: LedgerSafePostgresTrialShadowResult,
    queuedAt: number,
    dispatchStartedAt: number | null,
    queueDepth: number,
  ): void => {
    try {
      options.observer?.(result)
    } catch {
      // Observability is intentionally unable to affect either shadow or live edits.
    }
    if (!telemetryTarget || process.env.NODE_ENV === 'production') {
      return
    }
    const completedAt = now()
    const detail: Record<string, unknown> = {
      version: 1,
      operationId: boundedTelemetryId(result.operationId),
      chapterId: boundedTelemetryId(result.chapterId),
      trialId: boundedTelemetryId(result.trialId),
      status: result.status,
      queueDepth: Math.min(MAX_QUEUE_DEPTH, Math.max(0, queueDepth)),
      queueMs: boundedDuration(
        (dispatchStartedAt === null ? completedAt : dispatchStartedAt) - queuedAt,
      ),
      ackMs: boundedDuration(
        completedAt - (dispatchStartedAt === null ? completedAt : dispatchStartedAt),
      ),
      totalMs: boundedDuration(completedAt - queuedAt),
    }
    if (result.status === 'skipped') {
      detail.reason = result.reason
    } else if (result.status === 'failed') {
      detail.errorCode = result.error.code
      detail.httpStatus = result.error.status
    } else {
      detail.revision = result.revision
      detail.duplicate = result.duplicate
      detail.requestBytes = result.requestBytes
      if (result.serverTransactionMs !== undefined) {
        detail.serverTransactionMs = boundedDuration(result.serverTransactionMs)
      }
    }
    const event = createTelemetryEvent(detail)
    if (!event) {
      return
    }
    try {
      telemetryTarget.dispatchEvent(event)
    } catch {
      // Development telemetry is best-effort and intentionally stateless.
    }
  }

  const openCircuit = (key: string): void => {
    openCircuits.add(key)
    headRevisions.delete(key)
  }

  const defer = (): Promise<void> =>
    new Promise(resolve => {
      try {
        deferDispatch(resolve)
      } catch {
        globalThis.setTimeout(resolve, 0)
      }
    })

  const skipped = (
    input: LedgerSafePostgresTrialShadowInput,
    trialId: string | null,
    reason:
      | 'circuit_open'
      | 'disabled'
      | 'queue_full'
      | 'unsupported_operation',
  ): LedgerSafePostgresTrialShadowResult => ({
    status: 'skipped',
    operationId: input.event.id,
    chapterId: input.event.chapterId,
    trialId,
    reason,
  })

  const finishImmediately = (
    result: LedgerSafePostgresTrialShadowResult,
    queueDepth = 0,
  ): Promise<LedgerSafePostgresTrialShadowResult> => {
    const completedAt = now()
    notify(result, completedAt, completedAt, queueDepth)
    return Promise.resolve(result)
  }

  const fail = (
    input: LedgerSafePostgresTrialShadowInput,
    trialId: string,
    error: LedgerSafePostgresTrialAPIError,
    postAttempts: 0 | 1 | 2,
  ): LedgerSafePostgresTrialShadowResult => ({
    status: 'failed',
    operationId: input.event.id,
    chapterId: input.event.chapterId,
    trialId,
    error,
    postAttempts,
  })

  const mirror = async (
    input: LedgerSafePostgresTrialShadowInput,
    trialId: string,
    kind: LedgerSafePostgresTrialOperationKind,
  ): Promise<LedgerSafePostgresTrialShadowResult> => {
    const key = queueKey(trialId, input.event.chapterId)
    let revision = headRevisions.get(key)
    if (revision === undefined) {
      const head = await getLedgerSafePostgresTrialHead(
        trialId,
        input.event.chapterId,
        options,
      )
      if (!head.ok) {
        return fail(input, trialId, head.error, 0)
      }
      revision = head.value.revision
      headRevisions.set(key, revision)
    }

    const upsertedRowIds = new Set(input.event.afterRows.map(row => row.id))
    const request = {
      chapterId: input.event.chapterId,
      operationId: input.event.id,
      expectedRevision: revision,
      kind,
      rowUpserts: input.event.afterRows,
      rowDeletes: Array.from(new Set(
        input.event.beforeRows
          .filter(row => !upsertedRowIds.has(row.id))
          .map(row => row.id),
      )),
      editorialEvent: input.event,
    }
    let postAttempts: 1 | 2 = 1
    let edit = await postLedgerSafePostgresTrialEdit(trialId, request, options)
    let conflictRecovered = false

    if (
      !edit.ok &&
      edit.error.status === 409 &&
      edit.error.serverCode === 'stale_revision'
    ) {
      headRevisions.delete(key)
      const refreshedHead = await getLedgerSafePostgresTrialHead(
        trialId,
        input.event.chapterId,
        options,
      )
      if (!refreshedHead.ok) {
        return fail(input, trialId, refreshedHead.error, 1)
      }
      headRevisions.set(key, refreshedHead.value.revision)
      postAttempts = 2
      conflictRecovered = true
      edit = await postLedgerSafePostgresTrialEdit(
        trialId,
        {
          ...request,
          expectedRevision: refreshedHead.value.revision,
        },
        options,
      )
    }

    if (!edit.ok) {
      headRevisions.delete(key)
      return fail(input, trialId, edit.error, postAttempts)
    }

    headRevisions.set(key, edit.value.revision)
    return {
      status: 'mirrored',
      operationId: input.event.id,
      chapterId: input.event.chapterId,
      trialId,
      revision: edit.value.revision,
      duplicate: edit.value.duplicate,
      conflictRecovered,
      postAttempts,
      requestBytes: edit.value.requestBytes,
      ...(edit.value.serverTransactionMs !== undefined
        ? { serverTransactionMs: edit.value.serverTransactionMs }
        : {}),
    }
  }

  const enqueue = (
    input: LedgerSafePostgresTrialShadowInput,
  ): Promise<LedgerSafePostgresTrialShadowResult> => {
    if (!input.enabled) {
      return finishImmediately(skipped(input, null, 'disabled'))
    }

    const kind = asSupportedKind(input.event.operation.kind)
    if (!kind) {
      return finishImmediately(skipped(input, null, 'unsupported_operation'))
    }

    let trialId = input.trialId?.trim() || null
    if (!trialId) {
      if (!generatedTrialId) {
        generatedTrialId = getOrCreateLedgerSafePostgresTrialId(
          storage,
          options.idFactory,
        )
      }
      trialId = generatedTrialId
    }
    const activeTrialId = trialId
    const key = queueKey(activeTrialId, input.event.chapterId)
    const pendingCount = pendingCounts.get(key) || 0
    if (openCircuits.has(key)) {
      return finishImmediately(
        skipped(input, activeTrialId, 'circuit_open'),
        pendingCount,
      )
    }
    if (pendingCount >= maxQueueDepth) {
      openCircuit(key)
      return finishImmediately(
        skipped(input, activeTrialId, 'queue_full'),
        pendingCount,
      )
    }

    const queueDepth = pendingCount + 1
    pendingCounts.set(key, queueDepth)
    const queuedAt = now()
    let dispatchStartedAt: number | null = null
    const previous = queueTails.get(key) || Promise.resolve()
    const completed = previous
      .then(() => defer())
      .then(() => {
        dispatchStartedAt = now()
        if (openCircuits.has(key)) {
          return skipped(input, activeTrialId, 'circuit_open')
        }
        return mirror(input, activeTrialId, kind)
      })
      .catch(error => fail(input, activeTrialId, unexpectedError(error), 0))
      .then(result => {
        if (result.status === 'failed') {
          openCircuit(key)
        }
        notify(result, queuedAt, dispatchStartedAt, queueDepth)
        return result
      })
      .finally(() => {
        const nextPendingCount = Math.max(
          0,
          (pendingCounts.get(key) || 1) - 1,
        )
        if (nextPendingCount === 0) {
          pendingCounts.delete(key)
        } else {
          pendingCounts.set(key, nextPendingCount)
        }
      })
    const tail = completed.then(() => undefined)
    queueTails.set(key, tail)
    void tail.finally(() => {
      if (queueTails.get(key) === tail) {
        queueTails.delete(key)
      }
    })
    return completed
  }

  return { enqueue }
}

const defaultShadow = createLedgerSafePostgresTrialShadow()

export const enqueueLedgerSafePostgresTrialShadowEdit = (
  input: LedgerSafePostgresTrialShadowInput,
): Promise<LedgerSafePostgresTrialShadowResult> =>
  defaultShadow.enqueue(input)
